import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from '../components/ConfirmModal';
import { 
  Users, 
  UserCheck, 
  Shield, 
  Edit, 
  Trash2, 
  Search,
  AlertTriangle,
  Settings,
  Key,
  Mail,
  Target,
  ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const Administration = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const roles = [
    { value: 'membro', label: 'Membro', color: 'bg-secondary-color' },
    { value: 'secretario', label: 'Secretário', color: 'bg-primary-color' },
    { value: 'tesoureiro', label: 'Tesoureiro', color: 'bg-primary-color' },
    { value: 'pastor', label: 'Pastor', color: 'bg-warning-color' },
    { value: 'administracao', label: 'Administração', color: 'bg-error-color' }
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users', {
        params: {
          search: searchTerm,
          role: selectedRole
        }
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      loadUsers();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, selectedRole]);

  const handleRoleChange = async () => {
    if (!selectedUser || !newRole) return;

    try {
      await api.put(`/users/${selectedUser._id}/role`, {
        role: newRole
      });
      
      setShowRoleModal(false);
      setSelectedUser(null);
      setNewRole('');
      loadUsers();
    } catch (error) {
      console.error('Erro ao alterar perfil:', error);
      console.error('Erro ao alterar perfil do usuário');
    }
  };

  const handleDeleteUser = (userId) => {
    if (userId === user._id) {
      console.warn('Você não pode excluir sua própria conta');
      return;
    }
    const target = users.find(u => u._id === userId);
    setUserToDelete(target || { _id: userId });
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/users/${userToDelete._id}`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
    }
  };

  const openRoleModal = (userToEdit) => {
    // Apenas administradores podem alterar perfis
    if (user?.role !== 'administracao') {
      console.warn('Apenas administradores podem alterar perfis de usuários.');
      return;
    }
    setSelectedUser(userToEdit);
    setNewRole(userToEdit.role);
    setShowRoleModal(true);
  };

  const getRoleInfo = (roleValue) => {
    return roles.find(role => role.value === roleValue) || 
           { value: roleValue, label: roleValue, color: 'bg-gray-500' };
  };

  const getUserStats = () => {
    const stats = {
      total: users.length,
      byRole: {}
    };

    roles.forEach(role => {
      stats.byRole[role.value] = users.filter(u => u.role === role.value).length;
    });

    return stats;
  };

  const stats = getUserStats();

  if (user?.role !== 'administracao') {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Shield className="w-16 h-16 text-error-color mx-auto mb-4" />
          <h2 className="text-xl font-bold text-primary mb-2">Acesso Negado</h2>
          <p className="text-secondary">Você não tem permissão para acessar esta área.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-primary">Administração</h1>
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-primary-color" />
          <span className="text-xs sm:text-sm text-secondary">Painel Administrativo</span>
        </div>
      </div>

      {/* Navegação Rápida */}
      <div className="card">
        <div className="card-body">
          <h2 className="text-lg font-semibold text-primary mb-4">Navegação Rápida</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <a 
              href="/administracao/missoes" 
              className="flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Administrar Missões</p>
                  <p className="text-sm text-blue-600">Criar, editar e gerenciar missões</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="/administracao/voluntariado" 
              className="flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">Voluntariado</p>
                  <p className="text-sm text-green-600">Criar e gerenciar oportunidades</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-green-600 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="stats-grid">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-secondary">Total</p>
                <p className="text-lg sm:text-xl font-bold text-primary">{stats.total}</p>
              </div>
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-primary-color" />
            </div>
          </div>
        </div>

        {roles.map(role => (
          <div key={role.value} className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-secondary">{role.label}</p>
                  <p className="text-sm sm:text-lg font-bold text-primary">
                    {stats.byRole[role.value] || 0}
                  </p>
                </div>
                <div className={`w-3 h-3 rounded-full ${role.color}`}></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
          <div className="form-row">
            <div className="form-group">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou email..."
                  className="form-input pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <select
                className="form-select"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">Todos os perfis</option>
                {roles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Usuários */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Usuários do Sistema</h2>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading"></div>
              <span className="ml-2">Carregando...</span>
            </div>
          ) : users.length > 0 ? (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th className="text-xs sm:text-sm">Usuário</th>
                    <th className="text-xs sm:text-sm hidden sm:table-cell">Email</th>
                    <th className="text-xs sm:text-sm">Perfil</th>
                    
                    <th className="text-xs sm:text-sm hidden lg:table-cell">Cadastrado em</th>
                    <th className="text-xs sm:text-sm">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((userItem) => {
                    const roleInfo = getRoleInfo(userItem.role);
                    return (
                      <tr key={userItem._id}>
                        <td>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary-color rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-medium">
                              {userItem.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-xs sm:text-sm font-medium">{userItem.name}</p>
                              {userItem._id === user._id && (
                                <span className="text-xs text-primary-color">(Você)</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-secondary" />
                            <span className="text-xs sm:text-sm">{userItem.email}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`inline-block px-1 sm:px-2 py-1 text-xs rounded text-white ${roleInfo.color}`}>
                            {roleInfo.label}
                          </span>
                        </td>
                        <td className="hidden md:table-cell">

                        </td>
                        <td className="hidden lg:table-cell">
                          <span className="text-xs sm:text-sm">
                            {format(new Date(userItem.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                          </span>
                        </td>
                        <td>
                          <div className="flex gap-1 sm:gap-2">
                            {user?.role === 'administracao' && (
                              <button
                                onClick={() => openRoleModal(userItem)}
                                className="btn btn-sm btn-outline"
                                title="Alterar perfil"
                              >
                                <Key className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline ml-1">Perfil</span>
                              </button>
                            )}
                            {userItem._id !== user._id && (
                              <button
                                onClick={() => handleDeleteUser(userItem._id)}
                                className="btn btn-sm btn-danger"
                                title="Excluir usuário"
                              >
                                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline ml-1">Excluir</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-secondary">Nenhum usuário encontrado</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Alteração de Perfil */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
          <div className="modal-content">
            <div className="card-header">
              <h3 className="card-title">Alterar Perfil de Usuário</h3>
            </div>
            <div className="card-body space-y-4">
              <div className="flex items-center gap-3 p-3 bg-background-color rounded-md">
                <div className="w-10 h-10 bg-primary-color rounded-full flex items-center justify-center text-white font-medium">
                  {selectedUser.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-secondary">{selectedUser.email}</p>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Novo Perfil</label>
                <select
                  className="form-select"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>

              {newRole === 'administracao' && (
                <div className="flex items-start gap-2 p-3 bg-warning-color bg-opacity-10 rounded-md">
                  <AlertTriangle className="w-5 h-5 text-warning-color mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning-color">Atenção!</p>
                    <p className="text-xs text-secondary">
                      O perfil de Administração tem acesso total ao sistema.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="card-footer">
              <div className="btn-group">
                <button
                  onClick={() => {
                    setShowRoleModal(false);
                    setSelectedUser(null);
                    setNewRole('');
                  }}
                  className="btn btn-outline flex-1 sm:flex-none"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRoleChange}
                  className="btn btn-primary flex-1 sm:flex-none"
                  disabled={!newRole || newRole === selectedUser.role}
                >
                  <span className="hidden sm:inline">Alterar Perfil</span>
                  <span className="sm:hidden">Alterar</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir ${userToDelete?.name || 'este usuário'}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default Administration;
